import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import StaffLayout from "@/components/StaffLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Home } from "lucide-react";
import { fmtINR, fmtNum, fmtDate, fmtTime } from "@/utils/format";

import { useProducts, productColor } from "@/lib/products";

export default function ShiftSummary() {
  const { id } = useParams();
  const nav = useNavigate();
  const [shift, setShift] = useState(null);
  const products = useProducts(shift);

  useEffect(() => { api.get(`/shifts/${id}`).then((r) => setShift(r.data)); }, [id]);
  if (!shift) return <StaffLayout><div className="h-32 animate-pulse bg-slate-100 rounded-2xl" /></StaffLayout>;

  const t = shift.totals || {};
  const testing = shift.testing || {};
  const prices = shift.fuel_prices_used || {};

  return (
    <StaffLayout>
      <div className="flex items-center gap-2 mb-4">
        <CheckCircle2 className="w-6 h-6 text-emerald-600" />
        <h1 className="text-2xl font-display font-bold">Shift Summary</h1>
      </div>

      <Card className="p-5 rounded-2xl border-slate-200 mb-5">
        <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
          <Row label="Employee" value={`${shift.employee_name} (${shift.employee_code})`} />
          <Row label="Business Date" value={fmtDate(shift.business_date)} />
          <Row label="Shift" value={`Shift ${shift.shift_number}`} />
          <Row label="MPD" value={shift.mpd} />
          <Row label="Start" value={fmtTime(shift.start_time)} />
          <Row label="End" value={fmtTime(shift.end_time)} />
        </div>
      </Card>

      {products.map((p, i) => (
        <Card key={p.key} className="p-5 rounded-2xl border-slate-200 mb-4 border-l-4" style={{ borderLeftColor: productColor(i) }}>
          <div className="text-sm font-semibold text-slate-800 mb-3">{p.name}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-sm">
            <Row label="Opening" value={p.nozzles.map((nz) => Number(shift.opening?.[nz] || 0).toFixed(2)).join(" / ")} mono />
            <Row label="Closing" value={p.nozzles.map((nz) => Number(shift.closing?.[nz] || 0).toFixed(2)).join(" / ")} mono />
            <Row label="Gross" value={`${fmtNum(t[`gross_${p.key}`])} ${p.unit}`} mono />
            <Row label="Testing" value={`${fmtNum(testing[p.key])} ${p.unit}`} mono />
            <Row label="Net" value={`${fmtNum(t[`net_${p.key}`])} ${p.unit}`} mono />
            <Row label="Rate" value={fmtINR(prices[p.key])} mono />
            <Row label="Sales" value={fmtINR(t[`sales_${p.key}`])} strong mono />
          </div>
        </Card>
      ))}

      <Card className="p-6 rounded-2xl border-slate-200 bg-gradient-to-br from-[color:var(--bpcl-blue)] to-[color:var(--bpcl-blue-dark)] text-white">
        <div className="text-xs uppercase tracking-wide text-white/70 font-medium mb-3">Grand Summary</div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-[10px] text-white/70 uppercase">Gross</div>
            <div className="font-mono-num font-bold text-lg mt-1">{fmtNum(t.gross_total_litres)} L</div>
          </div>
          <div>
            <div className="text-[10px] text-white/70 uppercase">Testing</div>
            <div className="font-mono-num font-bold text-lg mt-1">{fmtNum(t.total_testing_litres)} L</div>
          </div>
          <div>
            <div className="text-[10px] text-white/70 uppercase">Net</div>
            <div className="font-mono-num font-bold text-lg mt-1">{fmtNum(t.net_total_litres)} L</div>
          </div>
        </div>
        <div className="border-t border-white/20 mt-5 pt-5 text-center">
          <div className="text-xs text-white/70 uppercase">Grand Sales</div>
          <div className="text-4xl font-bold font-mono-num mt-1" data-testid="grand-sales">{fmtINR(t.grand_sales)}</div>
        </div>
      </Card>

      {testing.remarks && (
        <Card className="p-4 rounded-2xl border-slate-200 mt-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Testing remarks</div>
          <div className="text-sm">{testing.remarks}</div>
        </Card>
      )}

      {(shift.next_employee_name || shift.previous_employee_name) && (
        <Card className="p-4 rounded-2xl border-slate-200 mt-4" data-testid="handover-info">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Shift Handover</div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {shift.previous_employee_name && (
              <>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
                  From <b>{shift.previous_employee_name}</b>
                </span>
                <span className="text-slate-400">→</span>
              </>
            )}
            <span className="rounded-full bg-[color:var(--bpcl-blue)]/10 text-[color:var(--bpcl-blue)] px-2.5 py-1 font-semibold">
              {shift.employee_name}
            </span>
            {shift.next_employee_name && (
              <>
                <span className="text-slate-400">→</span>
                <span className="rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-1">
                  To <b>{shift.next_employee_name}</b>
                </span>
              </>
            )}
          </div>
        </Card>
      )}

      <Button
        onClick={() => nav(`/shift/${shift.id}/print`)}
        data-testid="print-report-btn"
        className="w-full h-14 rounded-xl mt-6 bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)] text-base font-semibold"
      >
        Print Shift Report
      </Button>

      <Button
        onClick={() => nav("/dashboard")}
        data-testid="back-home-btn"
        variant="outline"
        className="w-full h-12 rounded-xl mt-2 text-base font-semibold"
      >
        <Home className="w-4 h-4 mr-2" /> Back to Dashboard
      </Button>
    </StaffLayout>
  );
}

function Row({ label, value, strong, mono }) {
  return (
    <div>
      <div className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`${strong ? "font-bold text-[color:var(--bpcl-blue)]" : "font-medium"} ${mono ? "font-mono-num" : ""}`}>{value ?? "-"}</div>
    </div>
  );
}
